/**
 * Video Review Tab Component (Phase 17.5 + 17.6)
 *
 * Layout container for the Video Review tab on the Assessment Detail page.
 * Shows:
 * - List of EvidenceClipCard components, one per clip
 * - Loading / empty / error states
 * - Overall feedback form at the bottom (1-5 star rating + comments + submit)
 * - Pre-populates from existing feedback data
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Video,
  Star,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import {
  useEvidenceClips,
  useAssessmentFeedback,
  useSubmitFeedback,
  type AssessmentFeedbackEntry,
} from '../../hooks/useAssessments';
import { EvidenceClipCard, type StepFeedbackState } from './EvidenceClipCard';

interface VideoReviewTabProps {
  assessmentId: string;
}

export function VideoReviewTab({ assessmentId }: VideoReviewTabProps) {
  const {
    data: clipsData,
    isLoading: clipsLoading,
    error: clipsError,
  } = useEvidenceClips(assessmentId);

  const {
    data: feedbackData,
    isLoading: feedbackLoading,
  } = useAssessmentFeedback(assessmentId);

  const submitFeedback = useSubmitFeedback();

  // Per-step feedback state managed here, keyed by stepId
  const [stepFeedbackMap, setStepFeedbackMap] = useState<Record<string, StepFeedbackState>>({});

  // Overall feedback state
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [overallComments, setOverallComments] = useState('');
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Pre-populate overall feedback from existing data
  const existingOverallFeedback = useMemo(() => {
    if (!feedbackData?.feedback) return null;
    return feedbackData.feedback.find(
      (f) => f.overallRating !== null && f.stepId === null && f.questionResponseId === null
    );
  }, [feedbackData?.feedback]);

  // Initialize overall state from existing feedback when it loads
  const [initializedFromExisting, setInitializedFromExisting] = useState(false);
  useEffect(() => {
    if (existingOverallFeedback && !initializedFromExisting) {
      setOverallRating(existingOverallFeedback.overallRating);
      setOverallComments(existingOverallFeedback.overallComments ?? '');
      setInitializedFromExisting(true);
    }
  }, [existingOverallFeedback, initializedFromExisting]);

  // Build a map of existing per-step feedback for quick lookup
  const existingStepFeedbackMap = useMemo(() => {
    const map: Record<string, AssessmentFeedbackEntry> = {};
    if (!feedbackData?.feedback) return map;
    for (const entry of feedbackData.feedback) {
      if (entry.stepId) {
        map[entry.stepId] = entry;
      }
    }
    return map;
  }, [feedbackData?.feedback]);

  const handleStepFeedbackChange = useCallback((state: StepFeedbackState) => {
    setStepFeedbackMap((prev) => ({
      ...prev,
      [state.stepId]: state,
    }));
  }, []);

  const handleSubmit = async () => {
    setSubmitSuccess(false);

    // Build per-step feedback entries (only those with an agree/disagree selection)
    const stepEntries = Object.values(stepFeedbackMap)
      .filter((s) => s.agreesWithAi !== null)
      .map((s) => ({
        stepId: s.stepId,
        agreesWithAi: s.agreesWithAi!,
        correctedScore: s.correctedScore,
        rationale: s.rationale || undefined,
      }));

    // Build overall feedback
    const overall =
      overallRating !== null
        ? {
            overallRating,
            overallComments: overallComments || undefined,
          }
        : undefined;

    // Must have at least one
    if (stepEntries.length === 0 && !overall) return;

    try {
      await submitFeedback.mutateAsync({
        assessmentId,
        data: {
          steps: stepEntries.length > 0 ? stepEntries : undefined,
          overall,
        },
      });
      setSubmitSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch {
      // Error is handled by TanStack Query and displayed via mutation state
    }
  };

  const clips = clipsData?.clips ?? [];
  const isLoading = clipsLoading || feedbackLoading;

  // Check if there's anything to submit
  const hasStepFeedback = Object.values(stepFeedbackMap).some(
    (s) => s.agreesWithAi !== null
  );
  const hasOverallFeedback = overallRating !== null;
  const canSubmit = hasStepFeedback || hasOverallFeedback;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-[#00674a] animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading evidence clips...</p>
      </div>
    );
  }

  // Error state
  if (clipsError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-3">
          <AlertCircle className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-sm font-medium text-gray-700">Failed to load evidence clips</p>
        <p className="text-xs text-gray-500 mt-1">
          {clipsError instanceof Error ? clipsError.message : 'An error occurred'}
        </p>
      </div>
    );
  }

  // Empty state
  if (clips.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
          <Video className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-700">No evidence clips available</p>
        <p className="text-xs text-gray-500 mt-1">
          Evidence clips are generated automatically for failed steps during assessment processing.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Clip count header */}
      <div className="flex items-center gap-2">
        <Video className="w-4 h-4 text-[#00674a]" />
        <span className="text-sm font-semibold text-gray-700">
          {clips.length} evidence clip{clips.length !== 1 ? 's' : ''}
        </span>
        <span className="text-xs text-gray-400">
          (blurred for privacy)
        </span>
      </div>

      {/* Evidence Clip Cards */}
      <div className="space-y-4">
        {clips.map((clip) => {
          const stepId = clip.step?.id ?? clip.id;
          return (
            <EvidenceClipCard
              key={clip.id}
              clip={clip}
              existingFeedback={existingStepFeedbackMap[stepId]}
              feedbackState={stepFeedbackMap[stepId]}
              onFeedbackChange={handleStepFeedbackChange}
            />
          );
        })}
      </div>

      {/* Overall Feedback Form */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-[#00674a]/5 to-[#c5e8c2]/10">
          <h3 className="font-bold text-gray-900">Overall Assessment Feedback</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Rate the overall quality of the AI assessment
          </p>
        </div>

        <div className="p-5 space-y-4">
          {/* Star Rating */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              AI Assessment Quality
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((rating) => {
                const isActive =
                  (hoverRating !== null ? hoverRating : overallRating ?? 0) >= rating;
                return (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setOverallRating(rating)}
                    onMouseEnter={() => setHoverRating(rating)}
                    onMouseLeave={() => setHoverRating(null)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`Rate ${rating} out of 5`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        isActive
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                );
              })}
              {overallRating !== null && (
                <span className="text-sm font-medium text-gray-600 ml-2">
                  {overallRating}/5
                </span>
              )}
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Comments (optional)
            </label>
            <textarea
              value={overallComments}
              onChange={(e) => setOverallComments(e.target.value)}
              placeholder="Share your thoughts on the AI assessment quality, accuracy, and areas for improvement..."
              rows={3}
              maxLength={5000}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all resize-none"
            />
          </div>
        </div>

        {/* Submit Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {submitSuccess && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                Feedback saved
              </span>
            )}
            {submitFeedback.isError && (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600">
                <AlertCircle className="w-4 h-4" />
                Failed to save feedback
              </span>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitFeedback.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00674a] text-white text-sm font-semibold rounded-xl shadow-lg shadow-[#00674a]/25 hover:bg-[#00674a]/90 hover:shadow-xl hover:shadow-[#00674a]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitFeedback.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Feedback
          </button>
        </div>
      </div>

      {/* Existing overall feedback display */}
      {existingOverallFeedback && (
        <div className="p-4 bg-[#c5e8c2]/20 rounded-xl border border-[#c5e8c2]/40">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#00674a] uppercase tracking-wider mb-2">
            Previous Feedback
            {existingOverallFeedback.submittedByUser && (
              <span className="text-gray-400 font-normal normal-case">
                by {existingOverallFeedback.submittedByUser.fullName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 mb-1">
            {[1, 2, 3, 4, 5].map((rating) => (
              <Star
                key={rating}
                className={`w-4 h-4 ${
                  (existingOverallFeedback.overallRating ?? 0) >= rating
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-1">
              {existingOverallFeedback.overallRating}/5
            </span>
          </div>
          {existingOverallFeedback.overallComments && (
            <p className="text-sm text-gray-700 mt-1">
              {existingOverallFeedback.overallComments}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default VideoReviewTab;
